import { useEffect, useRef } from 'react';
import { mixpanelTracker } from '../utils/mixpanel-utility';

// Mapping of onboarding steps to their names
export const ONBOARDING_STEPS = {
  1: 'Welcome',
  2: 'Connect Meta Account',
  3: 'Connect Social Accounts',
  4: 'AutoMod Introduction',
  5: 'AutoMod Configuration - Content Types',
  6: 'AutoMod Configuration - Hide Comments',
  7: 'AutoMod Configuration - Show Comments',
  8: 'AutoMod Configuration - Links',
  9: 'AutoMod Settings Review',
  10: 'Onboarding Complete'
};

export type OnboardingStep = keyof typeof ONBOARDING_STEPS;

interface UseOnboardingTrackingProps {
  currentStep: OnboardingStep;
  userId?: string | number | null;
  currentSocial?: any;
}

export const useOnboardingTracking = ({
  currentStep,
  userId,
  currentSocial
}: UseOnboardingTrackingProps) => {
  const previousStepRef = useRef<OnboardingStep | null>(null);
  const isIdentifiedRef = useRef(false);
  
  // Identify user when the hook is first used if we have user info
  useEffect(() => {
    if (userId && !isIdentifiedRef.current) {
        const userString = localStorage.getItem("user");
        let user;
        try {
          user = JSON.parse(userString || '{}');
        } catch (error) {
          console.error("Error parsing user from localStorage:", error);
          user = {};
        }
        console.log(user.name)
        mixpanelTracker.identifyUser(user, currentSocial);
      isIdentifiedRef.current = true;
    }
  }, [userId, currentSocial]);
  
  // Track step changes
  useEffect(() => {
    // Only track if we have step data
    if (!currentStep || !ONBOARDING_STEPS[currentStep]) return;
    
    // If this is a new step (not just a re-render of the same step)
    if (previousStepRef.current !== currentStep) {
      const stepName = ONBOARDING_STEPS[currentStep];
      
      // Track the new step
      mixpanelTracker.trackOnboardingStep(Number(currentStep), stepName, {
        from_step: previousStepRef.current ? Number(previousStepRef.current) : null,
        from_step_name: previousStepRef.current ? ONBOARDING_STEPS[previousStepRef.current] : null
      });
      
      // Check if this is the final step
      if (currentStep === 10) {
        mixpanelTracker.trackOnboardingComplete();
      }
      
      // Update our ref to the current step
      previousStepRef.current = currentStep;
    }
  }, [currentStep]);
  
  // Return tracking functions that can be used in components
  return {
    trackAction: (action: string, properties?: Record<string, any>) => {
      mixpanelTracker.trackOnboardingAction(action, {
        current_step: currentStep,
        current_step_name: ONBOARDING_STEPS[currentStep],
        ...properties
      });
    },
    
    trackAbandonment: (reason?: string) => {
      if (!currentStep) return;
      
      mixpanelTracker.trackOnboardingAbandoned(
        Number(currentStep),
        ONBOARDING_STEPS[currentStep],
        { reason }
      );
    }
  };
};